import type { MessageSeed } from '../engine/accumulator'
import type { AssistantMessage, ChatMessage } from '../types'

/**
 * Store content to seed a resuming message's fold from. The seed has to cover
 * exactly what the replay will not resend, so it follows the cursor.
 *
 * With a cursor the replay starts past what the store holds, so the stored
 * content seeds and the replayed tail extends it. Without one the replay is
 * the whole turn log, and seeding content would fold that log on top of
 * itself and double the streamed prefix. Seeding empty text instead marks the
 * fold seeded, which keeps the first replayed delta an upsert rather than a
 * second bubble, and lets the replay rebuild the content.
 */
export function seedFromMessages(
  messages: ReadonlyArray<ChatMessage> | undefined,
  messageId: string,
  contentSeed: boolean,
): MessageSeed | undefined {
  const existing = messages?.find((m) => m.id === messageId)
  if (!existing || existing.role !== 'assistant') return undefined
  if (!contentSeed) return { text: '' }
  const assistant = existing as AssistantMessage
  // The fold's `text` is the RAW delta concatenation; `content` is the copy
  // with inline `<think>` markup stripped. When the message carried markup,
  // seed the raw text (its markers intact, so a replayed mid-think stream
  // keeps extending consistently) and let the scan re-derive thinking —
  // seeding the extracted copy as `thinking` would freeze it, since
  // event-sourced thinking always wins over the inline scan.
  const seedText = typeof assistant.rawContent === 'string' ? assistant.rawContent : assistant.content
  return {
    ...(typeof seedText === 'string' ? { text: seedText } : {}),
    ...(typeof assistant.rawContent !== 'string' && typeof assistant.thinkingContent === 'string'
      ? { thinking: assistant.thinkingContent }
      : {}),
    ...(Array.isArray(assistant.contentParts) ? { parts: assistant.contentParts } : {}),
  }
}
